import React, { useState, useEffect } from 'react'
import { Rocket, Zap, Users, Wallet, Clock, Trophy, TrendingUp, DollarSign, Activity, BarChart3, Shield, ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { walletService } from '../services/walletService'
import { useLanguage } from '../i18n'
import MiningDashboard from '../components/MiningDashboard'
import FuelPoolDashboard from '../components/FuelPoolDashboard'
import TeamDashboard from '../components/TeamDashboard'
import AssetDashboard from '../components/AssetDashboard'
import { CONTRACT_CONFIG } from '../config/contract'

const StatCard = ({ icon: Icon, value, label, change, changeLabel, color = 'gold' }) => {
  const colorClasses = {
    gold: 'text-titanium-gold',
    blue: 'text-cyber-blue',
    green: 'text-success-green',
    cyan: 'text-tech-cyan'
  }

  return (
    <div className="stat-card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="stat-value">{value}</div>
          <div className="stat-label">{label}</div>
          {change && (
            <div className={`stat-change ${change >= 0 ? 'positive' : 'negative'}`}>
              {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4 rotate-180" />}
              <span>{Math.abs(change)}%</span>
              {changeLabel && <span className="ml-1 opacity-70">{changeLabel}</span>}
            </div>
          )}
        </div>
        <div className={`icon-tech ${colorClasses[color]}`}>
          <Icon className="w-7 h-7" />
        </div>
      </div>
    </div>
  )
}

const FeatureCard = ({ icon: Icon, title, description, path, stats }) => {
  return (
    <Link to={path} className="block">
      <div className="glass-card h-full hover:border-titanium-gold/40 cursor-pointer group">
        <div className="p-8">
          <div className="icon-tech mb-6 group-hover:scale-110 transition-transform">
            <Icon className="w-8 h-8" />
          </div>
          <h3 className="tech-title text-2xl font-bold mb-3">{title}</h3>
          <p className="tech-subtitle text-base mb-6">{description}</p>
          {stats && (
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-titanium-gold/10">
              {stats.map((stat, idx) => (
                <div key={idx}>
                  <div className="tech-number text-xl">{stat.value}</div>
                  <div className="text-sm text-gray-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

const HomePage = ({ isConnected, userAddress, balance, onConnect, onDisconnect }) => {
  const { t } = useLanguage()
  const [contractInfo, setContractInfo] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadContractData()
  }, [isConnected])

  const loadContractData = async () => {
    try {
      const contractData = await walletService.getContractInfo()
      setContractInfo(contractData)
    } catch (error) {
      console.error('加载合约数据失败:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const features = [
    {
      icon: Clock,
      title: t('features.mining.title'),
      description: t('features.mining.desc'),
      path: '/mining',
      stats: [
        { value: '12h', label: t('features.mining.timeWindow') },
        { value: t('features.mining.smartVerify'), label: '12h' }
      ]
    },
    {
      icon: Zap,
      title: t('features.fuel.title'),
      description: t('features.fuel.desc'),
      path: '/fuel',
      stats: [
        { value: 'Gas', label: t('features.fuel.autoRefund') },
        { value: t('features.fuel.dailyReward'), label: 'Daily' }
      ]
    },
    {
      icon: Users,
      title: t('features.team.title'),
      description: t('features.team.desc'),
      path: '/team',
      stats: [
        { value: t('features.team.weeklyRank'), label: 'Weekly' },
        { value: t('features.team.teamWork'), label: 'Team' }
      ]
    },
    {
      icon: Wallet,
      title: t('features.assets.title'),
      description: t('features.assets.desc'),
      path: '/assets',
      stats: [
        { value: '30U', label: t('features.assets.withdrawLimit') },
        { value: t('features.assets.onChain'), label: 'On-Chain' }
      ]
    }
  ]

  const keyMetrics = [
    { icon: Activity, value: '1,247', label: t('home.activeUsers'), change: 12.5, changeLabel: '24h', color: 'gold' },
    { icon: BarChart3, value: '2.4M', label: t('home.totalVolume'), change: 8.3, changeLabel: '7d', color: 'blue' },
    { icon: TrendingUp, value: '32.7%', label: 'APY', change: 2.1, changeLabel: 'Week', color: 'green' },
    { icon: DollarSign, value: '$1.2M', label: t('home.tvl'), change: 15.2, changeLabel: '30d', color: 'cyan' }
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-titanium-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl text-gray-300">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center py-12">
          <div className="inline-flex items-center space-x-2 bg-titanium-gold/10 border border-titanium-gold/30 rounded-full px-4 py-2 mb-8">
            <Shield className="w-4 h-4 text-titanium-gold" />
            <span className="text-sm font-semibold text-titanium-gold uppercase tracking-wider">{t('home.basedOnBSC')}</span>
          </div>
          <h1 className="tech-title text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight">
            {t('home.title')}
          </h1>
          <p className="tech-subtitle text-xl md:text-2xl max-w-4xl mx-auto mb-8 leading-relaxed">
            {t('home.subtitle')}
            <br />
            <span className="text-titanium-gold">{t('home.subtitleHighlight')}</span>
          </p>
          
          {/* Quick Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {!isConnected ? (
              <button
                onClick={onConnect}
                className="wallet-connect-btn text-lg px-12 py-4"
              >
                <Wallet className="w-6 h-6 mr-3" />
                {t('home.connectStart')}
              </button>
            ) : (
              <div className="flex items-center space-x-4">
                <div className="connection-status connected">
                  <Shield className="w-4 h-4" />
                  <span className="font-mono">
                    {userAddress.slice(0, 6)}...{userAddress.slice(-4)}
                  </span>
                </div>
                <div className="text-sm font-semibold text-titanium-gold bg-gradient-to-r from-titanium-gold/10 to-gold-light/10 px-4 py-2 rounded-lg">
                  {balance} STORM
                </div>
              </div>
            )}
            <Link
              to="/mining"
              className="btn-primary text-lg px-12 py-4"
            >
              <Activity className="w-6 h-6 mr-3" />
              {t('home.viewMining')}
            </Link>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center mb-10">
          <h2 className="tech-title text-4xl font-bold mb-3">{t('home.keyMetrics')}</h2>
          <p className="tech-subtitle text-lg">{t('home.keyMetricsDesc')}</p>
        </div>
        <div className="tech-grid tech-grid-4">
          {keyMetrics.map((metric, idx) => (
            <StatCard key={idx} {...metric} />
          ))}
        </div>
      </div>

      {/* Core Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center mb-12">
          <h2 className="tech-title text-4xl font-bold mb-3">{t('home.coreFeatures')}</h2>
          <p className="tech-subtitle text-lg">{t('home.coreFeaturesDesc')}</p>
        </div>
        <div className="tech-grid tech-grid-2">
          {features.map((feature, idx) => (
            <FeatureCard key={idx} {...feature} />
          ))}
        </div>
      </div>

      {/* System Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="glass-card gold-border">
          <div className="p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="tech-title text-4xl font-bold mb-3">{t('home.systemOverview')}</h2>
              <p className="tech-subtitle text-lg">{t('home.systemOverviewDesc')}</p>
            </div>
            <div className="tech-grid tech-grid-3">
              <div className="text-center p-6">
                <div className="icon-tech mx-auto mb-4">
                  <Shield className="w-7 h-7" />
                </div>
                <div className="tech-subtitle text-sm uppercase tracking-wider mb-2">{t('home.contractAddress')}</div>
                <div className="font-mono text-sm break-all bg-black/30 px-3 py-2 rounded-lg">
                  {CONTRACT_CONFIG.address}
                </div>
              </div>
              <div className="text-center p-6">
                <div className="icon-tech mx-auto mb-4">
                  <Activity className="w-7 h-7" />
                </div>
                <div className="tech-subtitle text-sm uppercase tracking-wider mb-2">{t('home.networkName')}</div>
                <div className="tech-title text-xl">{CONTRACT_CONFIG.network.chainName}</div>
              </div>
              <div className="text-center p-6">
                <div className="icon-tech mx-auto mb-4">
                  <BarChart3 className="w-7 h-7" />
                </div>
                <div className="tech-subtitle text-sm uppercase tracking-wider mb-2">{t('home.chainId')}</div>
                <div className="tech-title text-xl">{CONTRACT_CONFIG.network.chainId}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Highlights */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <div className="text-center mb-12">
          <h2 className="tech-title text-4xl font-bold mb-3">{t('home.investmentHighlights')}</h2>
          <p className="tech-subtitle text-lg">{t('home.investmentHighlightsDesc')}</p>
        </div>
        <div className="tech-grid tech-grid-3">
          <div className="glass-card p-8">
            <div className="badge badge-gold mb-4">{t('home.secure')}</div>
            <h3 className="tech-title text-2xl font-bold mb-4">{t('home.secureTitle')}</h3>
            <p className="tech-subtitle">{t('home.secureDesc')}</p>
          </div>
          <div className="glass-card p-8">
            <div className="badge badge-green mb-4">{t('home.profit')}</div>
            <h3 className="tech-title text-2xl font-bold mb-4">{t('home.profitTitle')}</h3>
            <p className="tech-subtitle">{t('home.profitDesc')}</p>
          </div>
          <div className="glass-card p-8">
            <div className="badge badge-blue mb-4">{t('home.innovation')}</div>
            <h3 className="tech-title text-2xl font-bold mb-4">{t('home.innovationTitle')}</h3>
            <p className="tech-subtitle">{t('home.innovationDesc')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomePage
